import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Folder — animated folder visual adapted from React Bits' open-source `Folder`
 * component (https://reactbits.dev). Renders a stylized folder shape (pure CSS,
 * no SVG) with up to three "papers" peeking out of the top. On hover the
 * papers nudge upward and the front flap skews open; on click the folder
 * fans the papers out and they scale on hover. Click again to close.
 *
 * Used by `<CollectionCard>` to visualize a collection's first three items as
 * a stack of papers — cover thumbnails of experiences, basically — that the
 * user can pop open inline. Standalone consumers can render any
 * `React.ReactNode[]` as the `items` prop.
 *
 * Props (matching the React Bits source so consumers can copy / paste demos):
 * - `color`         primary folder color. Default `"#5227FF"`.
 * - `size`          scale factor (1 = 100x80px). Default `1`.
 * - `items`         up to three React nodes; each becomes a paper. Default `[]`.
 * - `onItemClick`   optional callback fired when a paper is clicked **while
 *                   the folder is open**. Useful for routing on item click.
 *                   Click bubbling is stopped when this fires so the parent
 *                   surface (e.g. a Card with its own onClick) doesn't double-
 *                   fire on the same gesture.
 * - `onOpenChange`  optional callback fired when the folder opens or closes.
 * - `defaultOpen`   uncontrolled initial open state. Default `false`.
 * - `open`          controlled open state. Pair with `onOpenChange`.
 *
 * Reduced-motion: the open / close + hover transitions use Tailwind's regular
 * `transition-all` utilities, so users with `prefers-reduced-motion: reduce`
 * still see the layout change but without the `duration-300` interpolation.
 */

export interface FolderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  color?: string;
  size?: number;
  items?: React.ReactNode[];
  /** Notified when a paper is clicked while the folder is open. Click bubbling stops here. */
  onItemClick?: (index: number) => void;
  /** Notified when the folder transitions between open and closed. */
  onOpenChange?: (open: boolean) => void;
  /** Default open state for uncontrolled use. */
  defaultOpen?: boolean;
  /** Controlled open state. */
  open?: boolean;
  /** Click on the folder body (closed state) → toggles open. Default `true`. */
  toggleOnClick?: boolean;
}

const darkenColor = (hex: string, percent: number): string => {
  let color = hex.startsWith("#") ? hex.slice(1) : hex;
  if (color.length === 3) {
    color = color
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(color, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
};

const getOpenTransform = (index: number) => {
  if (index === 0) return "translate(-120%, -70%) rotate(-15deg)";
  if (index === 1) return "translate(10%, -70%) rotate(15deg)";
  if (index === 2) return "translate(-50%, -100%) rotate(5deg)";
  return "";
};

export const Folder = React.forwardRef<HTMLDivElement, FolderProps>(
  (
    {
      color = "#5227FF",
      size = 1,
      items = [],
      onItemClick,
      onOpenChange,
      defaultOpen = false,
      open: controlledOpen,
      toggleOnClick = true,
      className,
      ...rest
    },
    ref,
  ) => {
    const maxItems = 3;
    const papers: Array<React.ReactNode | null> = items.slice(0, maxItems);
    while (papers.length < maxItems) papers.push(null);

    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? Boolean(controlledOpen) : uncontrolledOpen;
    const setOpen = (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    };

    const [paperOffsets, setPaperOffsets] = React.useState<{ x: number; y: number }[]>(
      Array.from({ length: maxItems }, () => ({ x: 0, y: 0 })),
    );

    const folderBackColor = darkenColor(color, 0.08);
    const paper1 = darkenColor("#ffffff", 0.1);
    const paper2 = darkenColor("#ffffff", 0.05);
    const paper3 = "#ffffff";

    const handleClick = (e: React.MouseEvent) => {
      if (!toggleOnClick) return;
      e.stopPropagation();
      const next = !open;
      setOpen(next);
      if (open) {
        // Closing — reset paper offsets so they snap back to centered.
        setPaperOffsets(Array.from({ length: maxItems }, () => ({ x: 0, y: 0 })));
      }
    };

    const handlePaperMouseMove = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
      if (!open) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const offsetX = (e.clientX - centerX) * 0.15;
      const offsetY = (e.clientY - centerY) * 0.15;
      setPaperOffsets((prev) => {
        const next = [...prev];
        next[index] = { x: offsetX, y: offsetY };
        return next;
      });
    };

    const handlePaperMouseLeave = (_e: React.MouseEvent<HTMLDivElement>, index: number) => {
      setPaperOffsets((prev) => {
        const next = [...prev];
        next[index] = { x: 0, y: 0 };
        return next;
      });
    };

    const handlePaperClick = (e: React.MouseEvent, index: number) => {
      if (!open) return;
      e.stopPropagation();
      onItemClick?.(index);
    };

    const folderStyle: React.CSSProperties = {
      ["--folder-color" as string]: color,
      ["--folder-back-color" as string]: folderBackColor,
      ["--paper-1" as string]: paper1,
      ["--paper-2" as string]: paper2,
      ["--paper-3" as string]: paper3,
    };

    const scaleStyle: React.CSSProperties = { transform: `scale(${size})` };

    return (
      <div ref={ref} style={scaleStyle} className={cn("inline-block", className)} {...rest}>
        <div
          className={cn(
            "group relative transition-all duration-200 ease-in cursor-pointer",
            !open && "hover:-translate-y-2",
          )}
          style={{
            ...folderStyle,
            transform: open ? "translateY(-8px)" : undefined,
          }}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-pressed={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(e as unknown as React.MouseEvent);
            }
          }}
        >
          <div
            className="relative w-[100px] h-[80px] rounded-tl-0 rounded-tr-[10px] rounded-br-[10px] rounded-bl-[10px]"
            style={{ backgroundColor: folderBackColor }}
          >
            <span
              className="absolute z-0 bottom-[98%] left-0 w-[30px] h-[10px] rounded-tl-[5px] rounded-tr-[5px] rounded-bl-0 rounded-br-0"
              style={{ backgroundColor: folderBackColor }}
            />
            {papers.map((item, i) => {
              let sizeClasses = "";
              if (i === 0) sizeClasses = open ? "w-[70%] h-[80%]" : "w-[70%] h-[80%]";
              if (i === 1) sizeClasses = open ? "w-[80%] h-[80%]" : "w-[80%] h-[70%]";
              if (i === 2) sizeClasses = open ? "w-[90%] h-[80%]" : "w-[90%] h-[60%]";

              const transformStyle = open
                ? `${getOpenTransform(i)} translate(${paperOffsets[i].x}px, ${paperOffsets[i].y}px)`
                : undefined;

              return (
                <div
                  key={i}
                  onMouseMove={(e) => handlePaperMouseMove(e, i)}
                  onMouseLeave={(e) => handlePaperMouseLeave(e, i)}
                  onClick={(e) => handlePaperClick(e, i)}
                  className={cn(
                    "absolute z-20 bottom-[10%] left-1/2 transition-all duration-300 ease-in-out overflow-hidden",
                    !open ? "transform -translate-x-1/2 translate-y-[10%] group-hover:translate-y-0" : "hover:scale-110",
                    sizeClasses,
                  )}
                  style={{
                    ...(!open ? {} : { transform: transformStyle }),
                    backgroundColor: i === 0 ? paper1 : i === 1 ? paper2 : paper3,
                    borderRadius: "10px",
                  }}
                >
                  {item}
                </div>
              );
            })}
            <div
              className={cn(
                "absolute z-30 w-full h-full origin-bottom transition-all duration-300 ease-in-out",
                !open && "group-hover:[transform:skew(15deg)_scaleY(0.6)]",
              )}
              style={{
                backgroundColor: color,
                borderRadius: "5px 10px 10px 10px",
                ...(open && { transform: "skew(15deg) scaleY(0.6)" }),
              }}
            />
            <div
              className={cn(
                "absolute z-30 w-full h-full origin-bottom transition-all duration-300 ease-in-out",
                !open && "group-hover:[transform:skew(-15deg)_scaleY(0.6)]",
              )}
              style={{
                backgroundColor: color,
                borderRadius: "5px 10px 10px 10px",
                ...(open && { transform: "skew(-15deg) scaleY(0.6)" }),
              }}
            />
          </div>
        </div>
      </div>
    );
  },
);
Folder.displayName = "Folder";
